<template>
    <form-validation ref="exposedValidation">
        <flex-container :gap="Size.xl">
            <flex-container
                :screen-sizes="{
                    laptop: 2,
                }"
            >
                <data-group>
                    <data-label>
                        Persoonsgegevens
                    </data-label>
                    <data-value>
                        Geef de informatie op
                    </data-value>
                </data-group>
            </flex-container>

            <flex-container
                :screen-sizes="{
                    laptop: 8,
                }"
            >
                <!-- left side -->
                <flex-container
                    :screen-sizes="{
                        laptop: 5,
                    }"
                >
                    <flex-container
                        :screen-sizes="{
                            laptop: 6,
                        }"
                    >
                        <input-group v-model="form.firstName" :schema="string()">
                            <template #label>
                                <input-label>
                                    Voornaam
                                </input-label>
                            </template>
                            <template #input>
                                <text-input />
                            </template>
                        </input-group>
                    </flex-container>

                    <flex-container
                        :screen-sizes="{
                            laptop: 4,
                        }"
                    >
                        <input-group v-model="form.middleName" :schema="string()">
                            <template #label>
                                <input-label>
                                    Tussenvoegsel
                                </input-label>
                            </template>
                            <template #input>
                                <text-input />
                            </template>
                        </input-group>
                    </flex-container>

                    <input-group v-model="form.gender" :schema="object().nullable()">
                        <template #label>
                            <input-label>
                                Geslacht
                            </input-label>
                        </template>
                        <template #input>
                            <gender-input />
                        </template>
                    </input-group>

                    <input-group v-model="form.phoneNumber" :schema="string()">
                        <template #label>
                            <input-label>
                                Telefoonnummer
                            </input-label>
                        </template>
                        <template #input>
                            <text-input />
                        </template>
                    </input-group>

                    <input-group v-model="form.email" :schema="string().required()">
                        <template #label>
                            <input-label>
                                E-mailadres<mandatory-input />
                            </input-label>
                        </template>
                        <template #input>
                            <text-input />
                        </template>
                    </input-group>

                    <input-group
                        v-if="hasWinckBranding()"
                        v-model="form.realworksAccountManagerId"
                        :schema="string()"
                    >
                        <template #label>
                            <input-label>
                                Realworks accountmanager-ID
                            </input-label>
                        </template>
                        <template #input>
                            <text-input />
                        </template>
                    </input-group>

                    <button-text
                        v-if="props.userId"
                        :disabled="resetPasswordDisabled"
                        @click="sendNewPassword"
                    >
                        <text-decorator bold>
                            Wachtwoord opnieuw genereren
                        </text-decorator>
                    </button-text>
                </flex-container>

                <!-- right side -->
                <flex-container
                    :screen-sizes="{
                        laptop: 5,
                    }"
                >
                    <input-group v-model="form.lastName" :schema="string().required()">
                        <template #label>
                            <input-label>
                                Achternaam<mandatory-input />
                            </input-label>
                        </template>
                        <template #input>
                            <text-input />
                        </template>
                    </input-group>

                    <input-group v-model="form.birthDate" :schema="date().nullable()">
                        <template #label>
                            <input-label>
                                Geboortedatum
                            </input-label>
                        </template>
                        <template #input>
                            <date-input horizontal-position="center" vertical-position="bottom" />
                        </template>
                    </input-group>

                    <input-group v-model="form.mobilePhoneNumber" :schema="string()">
                        <template #label>
                            <input-label>
                                Mobiel telefoonnummer
                            </input-label>
                        </template>
                        <template #input>
                            <text-input />
                        </template>
                    </input-group>

                    <input-group v-model="form.teams" :schema="array().min(1).required()">
                        <template #label>
                            <input-label>
                                Vestigingen<mandatory-input />
                            </input-label>
                        </template>
                        <template #input>
                            <team-multi-select-input :teams="teams" />
                        </template>
                    </input-group>

                    <flex-container direction="column" justify-content="center">
                        <input-group v-model="form.hasAccess" inline>
                            <template #input="{ bindings }">
                                <flex-container align-items="center" :gap="Size.sm">
                                    <checkbox-input :label="'Toegang tot ' + (hasWinckBranding() ? 'Winck' : 'AskNoa')" />
                                    <div>
                                        <label :for="bindings.ids.input">
                                            Toegang tot {{ hasWinckBranding() ? 'Winck' : 'AskNoa' }}
                                        </label>
                                        <text-description>
                                            <text-decorator weight="light" :size="FontSize.xs">
                                                Gebruiker ontvangt e-mail met wachtwoord
                                            </text-decorator>
                                        </text-description>
                                    </div>
                                </flex-container>
                            </template>
                        </input-group>
                        <input-group v-model="form.isManager" inline>
                            <template #input="{ bindings }">
                                <flex-container align-items="center" :gap="Size.sm">
                                    <checkbox-input :label="'Geef ' + hasWinckBranding() ? 'Winck' : 'AskNoa' + ' beheerdersrechten'" />
                                    <label :for="bindings.ids.input">
                                        Geef {{ hasWinckBranding() ? 'Winck' : 'AskNoa' }} beheerdersrechten
                                    </label>
                                </flex-container>
                            </template>
                        </input-group>
                    </flex-container>
                </flex-container>

                <alert-block v-if="hasWinckBranding()" :color="'blue-500'">
                    <template #title>
                        <text-decorator weight="medium">
                            Als Winck samen met Realworks wordt gebruikt, moet het e-mailadres overeenkomen met dat van de betreffende Realworks-medewerker.
                        </text-decorator>
                    </template>
                </alert-block>
            </flex-container>
        </flex-container>
    </form-validation>
</template>

<script setup lang="ts">
    import { type ExposedFormValidationBindings, FontSize, NotificationBlock, Size, useInteractionBlocker, useNotify } from '@keyvue/one-ui';
    import { onMounted, type Ref, ref } from 'vue';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import { Team } from '@keyvue/one-api-client';
    import { array, date, object, string } from 'yup';
    import TeamMultiSelectInput from '@/company/team/components/TeamMultiSelectInput.vue';
    import GenderInput from '@/shared/components/input/GenderInput.vue';
    import axios from 'axios';
    import MandatoryInput from '@/shared/components/MandatoryInput.vue';
    import useAppBranding from '@/services/app-branding/composables/useAppBranding';

    export interface Form {
        firstName: string,
        middleName: string,
        lastName: string,
        gender: { value: string, label: string } | null,
        birthDate: Date | null,
        phoneNumber: string,
        mobilePhoneNumber: string,
        email: string,
        realworksAccountManagerId: string,
        teams: Team[],
        hasAccess: boolean,
        isManager: boolean,
    }

    export interface ExposedUserForm {
        form: Form,
        setForm: (values: Form) => void,
        formValidation: ExposedFormValidationBindings,
    }

    const props = defineProps<{
        userId?: string,
    }>();

    const { client, currentCompany } = useClient();
    const { notify } = useNotify();
    const { blockInteractionForLoad } = useInteractionBlocker();
    const { hasWinckBranding } = useAppBranding();

    const resetPasswordDisabled = ref(false);

    const teams: Ref<Team[]> = ref([]) as Ref<Team[]>;

    const exposedValidation = ref<ExposedFormValidationBindings | any>();

    const form = ref<Form>({
        firstName: '',
        middleName: '',
        lastName: '',
        gender: null,
        birthDate: null,
        phoneNumber: '',
        mobilePhoneNumber: '',
        email: '',
        realworksAccountManagerId: '',
        teams: [],
        hasAccess: false,
        isManager: false,
    });

    onMounted(async () => {
        const teamSubresources = await client.getSubResources(currentCompany.value, Team, 'teams');
        teams.value = teamSubresources.data as Team[];
    });

    function setForm(values: Form) {
        form.value = values;
    }

    async function sendNewPassword() {
        const baseUrl = import.meta.env.VITE_LEGACY_HOST;
        const token = client.session.accessToken;

        resetPasswordDisabled.value = true;

        const clearBlock = blockInteractionForLoad('Bezig met het aanvragen van een nieuw wachtwoord');

        try {
            await axios.post(`${baseUrl}/reset_user_password`, {
                userId: props.userId,
                password: (Math.random() + 1).toString(36).substring(2),
                origin: 'keyflow',
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            notify({
                component: NotificationBlock,
                props: {
                    title: 'Gelukt!',
                    text: 'We hebben een nieuw wachtwoord aangevraagd voor deze gebruiker.',
                    color: 'green-500',
                },
                duration: 4000,
            });
        } catch (e) {
            notify({
                component: NotificationBlock,
                props: {
                    title: 'Er is iets fout gegaan!',
                    text: 'We konden geen nieuw wachtwoord aanvragen voor deze gebruiker! Probeer het later nogmaals.',
                    color: 'red-500',
                },
                duration: 4000,
            });
        } finally {
            clearBlock();
            resetPasswordDisabled.value = false;
        }
    }

    defineExpose({
        form,
        setForm,
        formValidation: exposedValidation,
    });
</script>
